/**
 * Enhanced Email Validation Utility
 * 
 * Provides comprehensive email validation including:
 * - Format validation
 * - Disposable email detection
 * - Domain validation
 * - Common typo detection
 * - Profanity/inappropriate content filtering
 */

// Profanity/inappropriate words filter
const INAPPROPRIATE_WORDS = [
  'piss', 'shit', 'fuck', 'damn', 'hell', 'ass', 'bitch', 'bastard',
  'crap', 'dick', 'cock', 'pussy', 'whore', 'slut', 'fag', 'nazi',
  'kike', 'spic', 'chink', 'gook', 'retard', 'idiot', 'stupid',
  'dumb', 'moron', 'imbecile', 'cunt', 'twat', 'wanker', 'tosser',
  'pissmouth', 'shithead', 'fuckface', 'asshole', 'dickhead', 'cocksucker',
  'motherfucker', 'faggot', 'nigger', 'nigga', 'coon', 'wetback'
];

// List of known disposable email domains
const DISPOSABLE_EMAIL_DOMAINS = [
  'tempmail.com',
  '10minutemail.com',
  'guerrillamail.com',
  'mailinator.com',
  'throwaway.email',
  'temp-mail.org',
  'getnada.com',
  'mohmal.com',
  'yopmail.com',
  'sharklasers.com',
  'trashmail.com',
  'maildrop.cc',
  'mintemail.com',
  'fakeinbox.com',
  'dispostable.com',
  'emailondeck.com',
  'tempr.email',
  'getairmail.com',
  'meltmail.com',
  'melt.li',
  'inboxkitten.com',
  'getnada.com',
  'tmpmail.org',
  'tmpmail.net',
  'tmpmail.io',
  'fakemail.net',
  'fakemailgenerator.com',
  'mytrashmail.com',
  'spamgourmet.com',
  'spamhole.com',
  'spam.la',
  'spambox.us',
  'spamfree24.org',
  'spamfree24.de',
  'spamfree24.eu',
  'spamfree24.net',
  'spamfree24.com',
  'spamgourmet.com',
  'spamherelots.com',
  'spamhereplease.com',
  'spamhole.com',
  'spamify.com',
  'spam.la',
  'spambob.com',
  'spambob.net',
  'spambob.org',
  'spambog.com',
  'spambog.de',
  'spambog.net',
  'spambog.ru',
  'spambox.us',
  'spamday.com',
  'spamfree24.org',
  'spamfree24.de',
  'spamfree24.eu',
  'spamfree24.net',
  'spamfree24.com',
  'spamgourmet.com',
  'spamherelots.com',
  'spamhereplease.com',
  'spamhole.com',
  'spamify.com',
  'spam.la',
  'spamtraps.com',
  'spamtroll.net',
  'spamwc.de',
  'spamwc.ga',
  'spamwc.gq',
  'spamwc.ml',
  'spamwc.tk',
  'spamwc.xyz',
  'spamwc.de',
  'spamwc.ga',
  'spamwc.gq',
  'spamwc.ml',
  'spamwc.tk',
  'spamwc.xyz',
  'tempail.com',
  'tempalias.com',
  'tempe-mail.com',
  'tempemail.biz',
  'tempemail.com',
  'tempinbox.co.uk',
  'tempinbox.com',
  'tempmail.it',
  'tempmail2.com',
  'tempmailer.com',
  'tempmailer.de',
  'temp-mail.ru',
  'tempmailo.com',
  'temp-mail.org',
  'temp-mail.ru',
  'tempmail.us',
  'tempmailaddress.com',
  'tempmailer.com',
  'tempmailer.de',
  'tempmailo.com',
  'temp-mail.org',
  'temp-mail.ru',
  'tempmail.us',
  'tempmailaddress.com',
  'tempmailer.com',
  'tempmailer.de',
  'tempmailo.com',
  'temp-mail.org',
  'temp-mail.ru',
  'tempmail.us',
  'tempmailaddress.com'
];

// Suspicious or test domains
const SUSPICIOUS_DOMAINS = [
  'test.com',
  'example.com',
  'example.org',
  'example.net',
  'test.test',
  'invalid.com',
  'fake.com',
  'noreply.com',
  'no-reply.com'
];

// Common email typos mapping
const COMMON_TYPOS = {
  'gmai.com': 'gmail.com',
  'gmial.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gmaiil.com': 'gmail.com',
  'yahooo.com': 'yahoo.com',
  'yaho.com': 'yahoo.com',
  'yhoo.com': 'yahoo.com',
  'hotmial.com': 'hotmail.com',
  'hotmai.com': 'hotmail.com',
  'hotmali.com': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outllook.com': 'outlook.com',
  'outlok.com': 'outlook.com'
};

/**
 * Validate email format using regex
 * @param {string} email - Email address to validate
 * @returns {boolean} - True if format is valid
 */
const isValidEmailFormat = (email) => {
  // RFC 5322 compliant regex (simplified)
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email);
};

/**
 * Check for common email typos
 * @param {string} email - Email address to check
 * @returns {Object} - { hasTypo: boolean, suggestion?: string }
 */
const checkCommonTypos = (email) => {
  const parts = email.split('@');
  if (parts.length !== 2) {
    return { hasTypo: false };
  }

  const domain = parts[1].toLowerCase();
  if (COMMON_TYPOS[domain]) {
    return {
      hasTypo: true,
      suggestion: `${parts[0]}@${COMMON_TYPOS[domain]}`
    };
  }

  return { hasTypo: false };
};

/**
 * Check if email domain is disposable
 * @param {string} email - Email address to check
 * @returns {boolean} - True if domain is disposable
 */
const isDisposableEmail = (email) => {
  const parts = email.split('@');
  if (parts.length !== 2) {
    return false;
  }

  const domain = parts[1].toLowerCase();
  return DISPOSABLE_EMAIL_DOMAINS.includes(domain);
};

/**
 * Check if email domain is suspicious
 * @param {string} email - Email address to check
 * @returns {boolean} - True if domain is suspicious
 */
const isSuspiciousDomain = (email) => {
  const parts = email.split('@');
  if (parts.length !== 2) {
    return false;
  }

  const domain = parts[1].toLowerCase();
  return SUSPICIOUS_DOMAINS.includes(domain);
};

/**
 * Check for inappropriate/profanity content in email
 * @param {string} email - Email address to check
 * @returns {Object} - { hasInappropriate: boolean, word?: string }
 */
const checkInappropriateContent = (email) => {
  const lowerEmail = email.toLowerCase();

  for (const word of INAPPROPRIATE_WORDS) {
    // Check if word appears in email (domain or local part)
    if (lowerEmail.includes(word)) {
      return {
        hasInappropriate: true,
        word: word
      };
    }
  }

  return { hasInappropriate: false };
};

/**
 * Comprehensive email validation
 * @param {string} email - Email address to validate
 * @returns {Object} - Validation result with details
 */
export const validateEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return {
      valid: false,
      error: 'Email is required',
      details: []
    };
  }

  const trimmedEmail = email.trim().toLowerCase();
  const details = [];

  // Check for inappropriate content FIRST (before format check)
  const profanityCheck = checkInappropriateContent(trimmedEmail);
  if (profanityCheck.hasInappropriate) {
    return {
      valid: false,
      error: 'Email contains inappropriate content',
      details: ['Please use a professional email address']
    };
  }

  // Check format
  if (!isValidEmailFormat(trimmedEmail)) {
    return {
      valid: false,
      error: 'Invalid email format',
      details: ['Please enter a valid email address']
    };
  }

  // Check for typos
  const typoCheck = checkCommonTypos(trimmedEmail);
  if (typoCheck.hasTypo) {
    details.push(`Did you mean ${typoCheck.suggestion}?`);
  }

  // Check for disposable emails
  if (isDisposableEmail(trimmedEmail)) {
    return {
      valid: false,
      error: 'Disposable email addresses are not allowed',
      details: ['Please use a permanent email address'],
      suggestion: typoCheck.hasTypo ? typoCheck.suggestion : null
    };
  }

  // Check for suspicious domains
  if (isSuspiciousDomain(trimmedEmail)) {
    return {
      valid: false,
      error: 'Please use a valid email address',
      details: ['This email domain is not allowed'],
      suggestion: typoCheck.hasTypo ? typoCheck.suggestion : null
    };
  }

  // Additional checks
  const parts = trimmedEmail.split('@');
  if (parts.length === 2) {
    const [localPart, domain] = parts;

    // Check local part length
    if (localPart.length > 64) {
      details.push('Email username is too long');
    }

    // Check domain length
    if (domain.length > 255) {
      details.push('Email domain is too long');
    }

    // Check for consecutive dots
    if (localPart.includes('..') || domain.includes('..')) {
      return {
        valid: false,
        error: 'Invalid email format',
        details: ['Email cannot contain consecutive dots']
      };
    }

    // Check for leading/trailing dots
    if (localPart.startsWith('.') || localPart.endsWith('.') ||
      domain.startsWith('.') || domain.endsWith('.')) {
      return {
        valid: false,
        error: 'Invalid email format',
        details: ['Email cannot start or end with a dot']
      };
    }
  }

  return {
    valid: true,
    error: null,
    details: typoCheck.hasTypo ? details : [],
    suggestion: typoCheck.hasTypo ? typoCheck.suggestion : null
  };
};

/**
 * Real-time email validation for input fields
 * Returns validation state that can be used for UI feedback
 * @param {string} email - Email address to validate
 * @returns {Object} - Validation state for UI
 */
export const getEmailValidationState = (email) => {
  if (!email || email.trim().length === 0) {
    return {
      isValid: null, // null means not yet validated
      message: '',
      showWarning: false
    };
  }

  const validation = validateEmail(email);

  if (validation.valid) {
    return {
      isValid: true,
      message: validation.suggestion ? `Did you mean ${validation.suggestion}?` : '',
      showWarning: !!validation.suggestion
    };
  }

  return {
    isValid: false,
    message: validation.error,
    showWarning: false
  };
};

